'use client';

import { Icon } from '@/app_components/common/Icon';
import { Link } from '@/app_components/common/Link';
import type { Breadcrumb } from '@/app_lib/types/Article';
import { AnalyticsEvent, ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

const trackingData = {
  data: {
    // biome-ignore lint/style/useNamingConvention: <external data>
    contentPlacementCD: 'top breadcrumb navigation',
    eventAction: 'click breadcrumb link',
    eventCategory: 'navigation',
  },
};

interface BreadcrumbsProps {
  items: Breadcrumb[];
}

interface BreadcrumbProps extends Breadcrumb {
  className?: string;
  index: number;
  isEnabled?: boolean;
}

const BreadcrumbItem = ({ label, href, className, isEnabled = true, index }: BreadcrumbProps) => {
  return (
    <nav
      aria-label={`breadcrumb ${label}`}
      className={cn(
        'flex items-center typography-default md:typography-sm font-medium capitalize min-w-0 flex-1',
        className,
      )}
    >
      {href && isEnabled ? (
        <span
          className='group flex items-center gap-sm'
          data-isfirst={index === 0 ? 'true' : 'false'}
        >
          <Icon
            className='md:group-data-[isfirst=true]:hidden hidden md:flex icon-helper'
            icon='ButtonRight'
            mode='custom'
            size='sm'
          />
          <Link
            aria-label={`breadcrumb link ${label}`}
            className='whitespace-pre flex items-center gap-sm text-primary-hover transition-colors duration-200'
            href={href}
            trackingData={{ data: { ...trackingData.data, eventLabel: label } }}
          >
            <Icon
              className='md:hidden icon-helper'
              icon='ButtonLeft'
              mode='custom'
              size='sm'
            />
            {label}
          </Link>
        </span>
      ) : (
        <span className='text-secondary flex items-center gap-sm min-w-0'>
          <Icon
            className='hidden md:flex icon-helper'
            icon='ButtonRight'
            mode='custom'
            size='sm'
          />
          <span className='truncate flex-1'>{label}</span>
        </span>
      )}
    </nav>
  );
};

export const Breadcrumbs = ({ items }: BreadcrumbsProps) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label='breadcrumbs'
      className={cn('min-w-0')}
      data-cy={ContentPlacement.Breadcrumbs}
    >
      <span className='flex items-center justify-start gap-sm'>
        {items.map(({ label, href }, index) => {
          let mobileItem = false;
          if ((index === 1 && items.length > 1) || items.length === 1) {
            mobileItem = true;
          }
          if (items.length === 2 && index === 0) {
            mobileItem = true;
          } else if (items.length === 2) {
            mobileItem = false;
          }
          return (
            <span
              className={cn(
                'items-center gap-sm',
                !mobileItem && 'hidden md:flex',
                index === items.length - 1 && 'min-w-0 flex-1',
              )}
              key={label}
            >
              <BreadcrumbItem
                href={href}
                index={index}
                isEnabled={index !== items.length - 1}
                label={label}
              />
            </span>
          );
        })}
      </span>
    </nav>
  );
};
